import { getIsNativePlatform } from 'utils/client/mobile';

const API_ROOT = getIsNativePlatform() ? process.env.REMOTE_API_URL : process.env.APP_URL;
export const API_URL = `${API_ROOT}/api`;

/**
 * Base URL for the Cloudflare Workers API (cf.insiderfinance.io,
 * cf-staging.insiderfinance.io). Each Worker hosts its own path prefix:
 *
 *   ${CF_API_URL}/v1/gex/{ticker}     gex-api Worker
 *   ${CF_API_URL}/v1/<future>/...     future Workers
 *
 * Falls back to the in-app /api gateway (API_URL) when CF_API_URL is unset
 * — gives us a safe rollback path: drop CF_API_URL from an env to route
 * traffic back through Vercel without a code change.
 *
 * Same value across web and mobile (Capacitor) — the Worker is an external
 * HTTPS endpoint either way, no need for a REMOTE_API_URL equivalent.
 */
export const CF_API_URL = process.env.CF_API_URL || API_URL;

/**
 * The single geo lookup — real country/city/region/IP resolved at the Cloudflare edge, plus
 * the regional parity offer. Points at the geo Worker when CF_API_URL is set, and falls back
 * to the Next `/api/v1/geo` route when it is not (the same rollback lever as gex-api). One
 * call per session feeds the banner, usage_ping, and CurrentUserContext.
 */
export const GEO_API = `${CF_API_URL}/v1/geo`;

/**
 * The exchange trading calendar — full closures and early closes as published by the exchange,
 * served by the reference Worker at `${CF_API_URL}/v1/reference/calendar` (cf.insiderfinance.io in
 * prod, cf-staging.insiderfinance.io in staging). Sourced from the same tradedata origin the
 * realtime session guard reads, so every consumer agrees on "was the market open on date X" by
 * construction, and the vendor API key never reaches the browser.
 *
 * Falls back to the in-app /api gateway when CF_API_URL is unset — the same rollback lever as
 * gex-api and geo. There is no Next companion route for this path, so on that fallback the fetch
 * 404s and MarketCalendarProvider degrades to the computed rules in `usEquityCalendar`; that
 * failure is safe (a holiday the calendar can't derive still closes on the rules, and the worst an
 * outage does is leave us on the computed floor) and IS the rollback.
 */
export const REFERENCE_CALENDAR_API = `${CF_API_URL}/v1/reference/calendar`;

/**
 * WebSocket base for realtime feeds (realtime Worker), e.g.
 * `wss://cf.insiderfinance.io/v1/realtime`. Unset ⇒ realtime disabled
 * everywhere and all surfaces use their legacy polling paths. This is the
 * global kill switch — the same rollback pattern as CF_API_URL: drop
 * REALTIME_URL from an env to route every surface back through polling with
 * no code change.
 */
export const REALTIME_URL = process.env.REALTIME_URL || null;

/**
 * The developer API Worker (cf-workers/api), whose `/manage/v0` routes the developer portal
 * (`/my/api`) calls directly from the browser with the viewer's Firebase ID token. CORS on the
 * Worker admits only this app's origins (MANAGE_CORS_ORIGINS).
 *
 *   staging (until the release step): https://api-staging.insiderfinance.workers.dev
 *   production (at release, T17):     https://api.insiderfinance.io
 *
 * The portal and docs also require DEVELOPER_API_ENABLED=true. A URL by itself never releases them.
 */
export const DEVELOPER_API_URL = process.env.DEVELOPER_API_URL || null;
